import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowDownLeft,
    ArrowUpRight,
    Boxes,
    ChevronDown,
    Flame,
    PackagePlus,
    RotateCcw,
    ScanLine,
    UserCog,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { rpc } from "../../lib/rpc";
import { movementTypeLabel } from "../../lib/movementTypes";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";

const PRIMARY_ACTIONS = [
    { to: "/flows/entnahme", label: "Entnahme", icon: ArrowUpRight, color: "text-primary" },
    { to: "/flows/umlagerung", label: "Umlagerung", icon: Boxes, color: "text-info" },
    { to: "/flows/ausleihe", label: "Ausleihe", icon: UserCog, color: "text-secondary" },
    { to: "/flows/rueckgabe", label: "Rückgabe", icon: RotateCcw, color: "text-warning" },
];

const KPI_TILES = [
    { key: "total_articles", label: "Artikel" },
    { key: "open_loans", label: "Ausleihen", to: "/ausleihen" },
    { key: "overdue_loans", label: "Überfällig", to: "/ausleihen" },
    { key: "low_stock", label: "Niedrig" },
    { key: "critical_stock", label: "Kritisch" },
    { key: "open_restock", label: "Nachbest." },
];

export default function MobileDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";
    const [kpis, setKpis] = useState(null);
    const [recent, setRecent] = useState([]);
    const [kpiOpen, setKpiOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([rpc.get("/dashboard/kpis"), rpc.get("/movements?limit=5")]).then(
            ([k, m]) => {
                if (!alive) return;
                if (k.ok) setKpis(k.data);
                if (m.ok) setRecent(m.data || []);
            }
        );
        return () => {
            alive = false;
        };
    }, []);

    const display = useMemo(
        () => user?.name || (user?.email ? user.email.split("@")[0] : "Platzwart"),
        [user]
    );

    return (
        <section className="flex flex-col gap-5" data-testid="mobile-dashboard">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                    Hallo, {display}
                </h1>
            </div>

            {/* Hero: großer Scan-CTA */}
            <button
                type="button"
                onClick={() => navigate("/scan")}
                className="pw-tap relative flex h-28 w-full items-center justify-start gap-4 overflow-hidden rounded-2xl bg-primary px-6 text-left text-primary-foreground shadow-lg transition-transform active:scale-[0.98]"
                data-testid="dashboard-scan-hero"
            >
                <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-black/20" />
                <ScanLine className="relative h-10 w-10" strokeWidth={2.4} />
                <div className="relative flex flex-col">
                    <span className="text-xs uppercase tracking-[0.2em] opacity-80">
                        Scan starten
                    </span>
                    <span className="text-2xl font-bold leading-tight">
                        Fach · Artikel · Ausleihgegenstand
                    </span>
                </div>
            </button>

            {/* Staging-Warnung -- bleibt prominent */}
            {kpis?.staging_positions > 0 ? (
                <button
                    type="button"
                    onClick={() => {
                        // #135 Befund 9: direkt in die Umlagerung mit
                        // Staging-Quelle statt Umweg über den Aktionen-Hub.
                        try {
                            sessionStorage.setItem(
                                "pw:scan-prefill",
                                JSON.stringify({ stagingSource: true })
                            );
                        } catch (_e) { /* ignore */ }
                        navigate("/flows/umlagerung");
                    }}
                    className="pw-tap flex items-center gap-3 rounded-lg border border-warning/40 bg-[rgb(var(--warning-bg))] px-4 py-3 text-left"
                    data-testid="staging-warning"
                >
                    <Flame className="h-5 w-5 text-warning" />
                    <div className="flex-1">
                        <div className="text-sm font-semibold text-foreground">
                            Nicht final eingelagert: {kpis.staging_positions}{" "}
                            {kpis.staging_positions === 1 ? "Position" : "Positionen"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            Jetzt einlagern (Umlagerung)
                        </div>
                    </div>
                </button>
            ) : null}

            {/* Primär-Aktionen: 2x2 große Kacheln */}
            <div className="grid grid-cols-2 gap-3" data-testid="primary-actions">
                {PRIMARY_ACTIONS.map((a) => {
                    const Icon = a.icon;
                    return (
                        <button
                            key={a.to}
                            type="button"
                            onClick={() => navigate(a.to)}
                            className="pw-tap flex h-24 flex-col items-start justify-between rounded-2xl border border-border bg-card/60 p-4 text-left transition-colors hover:border-primary/60 active:bg-card/80"
                            data-testid={`primary-${a.label.toLowerCase()}`}
                        >
                            <Icon className={`h-6 w-6 ${a.color}`} strokeWidth={2.4} />
                            <span className="text-base font-semibold text-foreground">
                                {a.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Sekundär: Wareneingang (seltener) */}
            <button
                type="button"
                onClick={() => navigate("/flows/eingang")}
                className="pw-tap flex items-center gap-3 rounded-xl border border-border/60 bg-card/40 px-4 py-3 text-left hover:border-primary/40"
                data-testid="primary-wareneingang"
            >
                <PackagePlus className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">Wareneingang</div>
                    <div className="text-xs text-muted-foreground">
                        Lieferung annehmen &amp; einlagern
                    </div>
                </div>
                <ArrowDownLeft className="h-4 w-4 text-muted-foreground" />
            </button>

            {/* Letzte Bewegungen -- kompakt */}
            {recent.length > 0 ? (
                <div>
                    <button
                        type="button"
                        onClick={() => navigate("/verlauf")}
                        className="mb-2 flex w-full items-center justify-between text-xs uppercase tracking-[0.2em] text-muted-foreground"
                        data-testid="recent-open"
                    >
                        <span>Letzte Bewegungen</span>
                        <span className="text-primary">Alle anzeigen →</span>
                    </button>
                    <div className="flex flex-col gap-2" data-testid="recent-movements">
                        {recent.slice(0, 3).map((m) => (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() =>
                                    navigate(
                                        m.article_id
                                            ? `/verlauf?article=${m.article_id}`
                                            : "/verlauf"
                                    )
                                }
                                className="pw-tap flex items-center gap-3 rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-left hover:border-primary/40"
                                data-testid={`movement-${m.id}`}
                            >
                                <div className="text-xs uppercase tracking-wider text-primary">
                                    {movementTypeLabel(m.type)}
                                </div>
                                <div className="flex-1 truncate text-xs text-foreground">
                                    {m.comment || m.reference_value || m.reason || "—"}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {m.quantity ?? ""}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            {/* KPI-Block nur einklappbar anzeigen (Admin-Info, nicht Monteur) */}
            {isAdmin && kpis ? (
                <Collapsible open={kpiOpen} onOpenChange={setKpiOpen}>
                    <CollapsibleTrigger asChild>
                        <button
                            type="button"
                            className="flex w-full items-center justify-between rounded-lg border border-border/60 bg-card/30 px-4 py-3 text-left"
                            data-testid="kpi-toggle"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                    Admin-KPIs
                                </div>
                                <div className="text-xs text-foreground">
                                    Bestand &amp; Betriebsmetriken
                                </div>
                            </div>
                            <ChevronDown
                                className={`h-4 w-4 text-muted-foreground transition-transform ${
                                    kpiOpen ? "rotate-180" : ""
                                }`}
                            />
                        </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="mt-3 grid grid-cols-3 gap-2" data-testid="kpi-grid">
                            {KPI_TILES.map((tile) => {
                                const Tag = tile.to ? "button" : "div";
                                return (
                                    <Tag
                                        key={tile.key}
                                        {...(tile.to
                                            ? { type: "button", onClick: () => navigate(tile.to) }
                                            : {})}
                                        className={`rounded-lg border border-border/60 bg-card/40 p-3 text-left ${tile.to ? "pw-tap" : ""}`}
                                        data-testid={`kpi-${tile.key}`}
                                    >
                                        <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                            {tile.label}
                                        </div>
                                        <div className="mt-1 text-xl font-black text-foreground">
                                            {kpis?.[tile.key] ?? 0}
                                        </div>
                                    </Tag>
                                );
                            })}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            ) : null}
        </section>
    );
}
